/*!

=========================================================
* Black Dashboard React v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this copyright notice shall be included in all copies or substantial portions of the Software.

*/
import { useEffect, useState, useContext, useRef } from "react";
// nodejs library that concatenates classes
import classNames from "classnames";

// reactstrap components
import {
  Button,
  Collapse,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Input,
  NavbarBrand,
  Navbar,
  NavLink,
  Nav,
  NavItem,
  Container,
  Modal,
  NavbarToggler,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ApiContext } from "../../contexts/ApiContext";
import { useNotification } from "../../contexts/NotificationContext";
import OrderAlertBell from "../OrderAlerts/OrderAlertBell";
import { getCardapioUrl } from "../../utils/cardapioUrl";
import { accountPath } from "../../utils/accountPath";
import UploadService from "../../service/upload.service";
import UserService from "../../service/user.service";
import mirandaIcon from "assets/img/miranda-mark.svg";

const MAX_AVATAR_MB = 2;
const MAX_AVATAR_BYTES = MAX_AVATAR_MB * 1024 * 1024;

function AdminNavbar(props) {
  const [collapseOpen, setcollapseOpen] = useState(false);
  const [modalSearch, setmodalSearch] = useState(false);
  const [color, setcolor] = useState("navbar-transparent");
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { accountId: accountIdParam } = useParams();
  const accountId = accountIdParam ? Number(accountIdParam) : null;
  const { notify } = useNotification();

  useEffect(() => {
    window.addEventListener("resize", updateColor);
    // Specify how to clean up after this effect:
    return function cleanup() {
      window.removeEventListener("resize", updateColor);
    };
  });
  // function that adds color white/transparent to the navbar on resize (this is for the collapse)
  const updateColor = () => {
    if (window.innerWidth < 993 && collapseOpen) {
      setcolor("bg-white");
    } else {
      setcolor("navbar-transparent");
    }
  };
  // this function opens and closes the collapse on small devices
  const toggleCollapse = () => {
    if (collapseOpen) {
      setcolor("navbar-transparent");
    } else {
      setcolor("bg-white");
    }
    setcollapseOpen(!collapseOpen);
  };
  // this function is to open the Search modal
  const toggleModalSearch = () => {
    setmodalSearch(!modalSearch);
  };

  const { logout: ctxLogout, isSuperAdmin, account, homeAccountId, user, getUser } = useContext(ApiContext);
  const isImpersonating =
    isSuperAdmin && homeAccountId && accountId && accountId !== homeAccountId;
  const cardapioUrl = getCardapioUrl(account?.menuCode);
  const avatarSrc = user?.avatarUrl || mirandaIcon;

  const logout = () => {
    if (ctxLogout) ctxLogout();
    navigate('/login');
  };

  const openProfileModal = () => {
    setSelectedFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setProfileModalOpen(true);
  };

  const closeProfileModal = () => {
    setProfileModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notify("tr", "warning", "Selecione um arquivo de imagem", "icon-alert-circle-exc");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      notify(
        "tr",
        "warning",
        `A imagem deve ter no máximo ${MAX_AVATAR_MB} MB`,
        "icon-alert-circle-exc"
      );
      e.target.value = "";
      setSelectedFile(null);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }

    setSelectedFile(file);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSaveAvatar = async () => {
    if (!selectedFile || !user?.id) return;
    setUploading(true);
    try {
      const res = await UploadService.uploadSingle(selectedFile);
      await UserService.update(user.id, { avatarUrl: res.url });
      await getUser(user.id);
      notify("tr", "success", "Foto de perfil atualizada", "icon-check-2");
      closeProfileModal();
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao atualizar foto de perfil";
      notify("tr", "danger", Array.isArray(msg) ? msg.join(", ") : msg, "icon-alert-circle-exc");
    } finally {
      setUploading(false);
    }
  };

  const displayPreview = previewUrl || avatarSrc;

  return (
    <>
      <Navbar className={classNames("navbar-absolute", color)} expand="lg">
        <Container fluid>
          <div className="navbar-wrapper">
            <div
              className={classNames("navbar-toggle d-inline", {
                toggled: props.sidebarOpened,
              })}
            >
              <NavbarToggler onClick={props.toggleSidebar}>
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </NavbarToggler>
            </div>
            <NavbarBrand href="#pablo" onClick={(e) => e.preventDefault()}>
              {props.brandText}
            </NavbarBrand>
          </div>
          {isImpersonating && (
            <div className="navbar-super-admin-actions d-none d-lg-flex">
              <Button
                color="primary"
                size="sm"
                className="btn-simple"
                onClick={() => navigate('/superadmin')}
              >
                Voltar ao painel
              </Button>
              <Button
                color="default"
                size="sm"
                outline
                onClick={() => navigate(accountPath(homeAccountId, '/dashboard'))}
              >
                Minha conta
              </Button>
            </div>
          )}
          <NavbarToggler onClick={toggleCollapse}>
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
          </NavbarToggler>
          <Collapse navbar isOpen={collapseOpen}>
            {isImpersonating && (
              <div className="navbar-super-admin-actions d-lg-none mb-3">
                <Button
                  color="primary"
                  size="sm"
                  className="btn-simple mr-2"
                  onClick={() => navigate('/superadmin')}
                >
                  Voltar ao painel
                </Button>
                <Button
                  color="default"
                  size="sm"
                  outline
                  onClick={() => navigate(accountPath(homeAccountId, '/dashboard'))}
                >
                  Minha conta
                </Button>
              </div>
            )}
            <Nav className="ml-auto navbar-actions" navbar>
              <OrderAlertBell />
              {cardapioUrl && (
                <NavItem className="nav-item">
                  <NavLink
                    className="nav-link"
                    href={cardapioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Cardápio digital"
                  >
                    <i className="tim-icons icon-notes" />
                  </NavLink>
                </NavItem>
              )}
              <UncontrolledDropdown nav>
                <DropdownToggle
                  caret
                  color="default"
                  nav
                  onClick={(e) => e.preventDefault()}
                >
                  <div className="photo">
                    <img alt="Perfil" src={avatarSrc} style={{ height: "100%", objectFit: "cover" }} />
                  </div>
                  <b className="caret d-none d-lg-block d-xl-block" />
                  <p  className="d-lg-none">Log out</p>
                </DropdownToggle>
                <DropdownMenu className="dropdown-navbar" right tag="ul">
                  <NavLink tag="li" onClick={openProfileModal}>
                    <DropdownItem className="nav-item">Perfil</DropdownItem>
                  </NavLink>
                  <NavLink tag="li" onClick={() => navigate(accountPath(accountId, '/configuracoes/conta'))}>
                    <DropdownItem className="nav-item">Configurações da conta</DropdownItem>
                  </NavLink>
                  {isSuperAdmin && (
                    <NavLink tag="li" onClick={() => navigate('/superadmin')}>
                      <DropdownItem className="nav-item">Super Admin</DropdownItem>
                    </NavLink>
                  )}
                  <DropdownItem divider tag="li" />
                  <NavLink tag="li" onClick={logout}>
                    <DropdownItem className="nav-item">Deslogar</DropdownItem>
                  </NavLink>
                </DropdownMenu>
              </UncontrolledDropdown>
              <li className="separator d-lg-none" />
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
      <Modal
        modalClassName="modal-search"
        isOpen={modalSearch}
        toggle={toggleModalSearch}
      >
        <ModalHeader>
          <Input placeholder="SEARCH" type="text" />
          <button
            aria-label="Close"
            className="close"
            onClick={toggleModalSearch}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
        </ModalHeader>
      </Modal>

      <Modal isOpen={profileModalOpen} toggle={closeProfileModal}>
        <ModalHeader toggle={closeProfileModal}>Foto de perfil</ModalHeader>
        <ModalBody>
          <div className="d-flex flex-column align-items-center mb-3">
            <img
              src={displayPreview}
              alt="Prévia"
              style={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #e3e3e3",
              }}
            />
          </div>
          <FormGroup>
            <Label for="avatar-upload">Enviar imagem</Label>
            <Input
              id="avatar-upload"
              type="file"
              accept="image/*"
              innerRef={fileInputRef}
              onChange={handleAvatarFileChange}
              disabled={uploading}
            />
            <small className="text-muted d-block mt-2">
              Formatos de imagem. Tamanho máximo: {MAX_AVATAR_MB} MB.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="pt-3 pb-3 px-3">
          <Button color="secondary" onClick={closeProfileModal} disabled={uploading}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleSaveAvatar}
            disabled={!selectedFile || uploading}
          >
            {uploading ? "Salvando..." : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default AdminNavbar;
